import type { GroupLayer, Layer, SurfaceLayer } from './types';

/** The fields that can be changed on any layer kind. */
export type LayerPatch = Partial<
  Pick<
    Layer,
    | 'name'
    | 'visible'
    | 'opacity'
    | 'blendMode'
    | 'blendChannels'
    | 'blendIf'
    | 'locked'
    | 'lockPixels'
    | 'lockPosition'
    | 'lockMask'
    | 'hasMask'
    | 'maskEnabled'
    | 'maskLinked'
    | 'clipping'
    | 'vectorMask'
    | 'effects'
    | 'contentOffset'
  >
>;

/** Spreading a patch over a union widens `kind`, so the result is re-narrowed. */
export function applyLayerPatch<T extends Layer>(layer: T, patch: LayerPatch): T {
  return { ...layer, ...patch } as T;
}

export function isGroup(layer: Layer): layer is GroupLayer {
  return layer.kind === 'group';
}

export function isSurfaceLayer(layer: Layer): layer is SurfaceLayer {
  return layer.kind !== 'group' && layer.kind !== 'adjustment' && layer.kind !== 'fill';
}

export function isFillLayer(layer: Layer): layer is import('./types').FillLayer {
  return layer.kind === 'fill';
}

export function childrenOf(layer: Layer): readonly Layer[] {
  return isGroup(layer) ? layer.children : [];
}

/** Where a layer sits in the tree, everything a move or an insert needs. */
export interface LayerLocation {
  layer: Layer;
  parent: GroupLayer | null;
  siblings: readonly Layer[];
  index: number;
}

/** Visits every layer bottom-up, a group before its own children. */
export function walkLayers(
  layers: readonly Layer[],
  visit: (layer: Layer, parent: GroupLayer | null, depth: number) => void,
  parent: GroupLayer | null = null,
  depth = 0,
): void {
  for (const layer of layers) {
    visit(layer, parent, depth);
    if (isGroup(layer)) walkLayers(layer.children, visit, layer, depth + 1);
  }
}

export function flattenLayers(layers: readonly Layer[]): Layer[] {
  const out: Layer[] = [];
  walkLayers(layers, (layer) => out.push(layer));
  return out;
}

/** Ids that need a GPU surface allocated for them. */
export function surfaceLayerIds(layers: readonly Layer[]): string[] {
  return flattenLayers(layers)
    .filter(isSurfaceLayer)
    .map((layer) => layer.id);
}

export function countLayers(layers: readonly Layer[]): number {
  let total = 0;
  walkLayers(layers, () => {
    total += 1;
  });
  return total;
}

export function locateLayer(layers: readonly Layer[], layerId: string): LayerLocation | null {
  const index = layers.findIndex((layer) => layer.id === layerId);
  if (index >= 0) return { layer: layers[index], parent: null, siblings: layers, index };

  for (const layer of layers) {
    if (!isGroup(layer)) continue;
    const nested = locateLayer(layer.children, layerId);
    if (nested) return { ...nested, parent: nested.parent ?? layer };
  }
  return null;
}

export function findLayerById(layers: readonly Layer[], layerId: string | null): Layer | null {
  if (!layerId) return null;
  return locateLayer(layers, layerId)?.layer ?? null;
}

/** True when `layerId` is the group itself or lives anywhere inside it. */
export function containsLayer(group: GroupLayer, layerId: string): boolean {
  if (group.id === layerId) return true;
  return locateLayer(group.children, layerId) !== null;
}

/** Replaces one layer in place, rebuilding only the branches on its path. */
export function mapLayer(
  layers: readonly Layer[],
  layerId: string,
  updater: (layer: Layer) => Layer,
): Layer[] {
  let changed = false;
  const next = layers.map((layer) => {
    if (layer.id === layerId) {
      changed = true;
      return updater(layer);
    }
    if (!isGroup(layer)) return layer;
    const children = mapLayer(layer.children, layerId, updater);
    if (children === layer.children) return layer;
    changed = true;
    return { ...layer, children };
  });
  return changed ? next : (layers as Layer[]);
}

export function removeLayerFromTree(
  layers: readonly Layer[],
  layerId: string,
): { layers: Layer[]; removed: Layer | null } {
  let removed: Layer | null = null;
  const next: Layer[] = [];

  for (const layer of layers) {
    if (layer.id === layerId) {
      removed = layer;
      continue;
    }
    if (isGroup(layer)) {
      const inner = removeLayerFromTree(layer.children, layerId);
      if (inner.removed) {
        removed = inner.removed;
        next.push({ ...layer, children: inner.layers });
        continue;
      }
    }
    next.push(layer);
  }
  return { layers: next, removed };
}

export interface InsertTarget {
  /** `null` inserts at the document root. */
  parentId: string | null;
  /**
   * Position among the siblings, 0 being the bottom. For a move this counts in
   * the list as it looks *before* the layer is taken out, so dropping above the
   * top-most sibling means its index plus one.
   */
  index: number;
}

export function insertLayerAt(
  layers: readonly Layer[],
  target: InsertTarget,
  layer: Layer,
): Layer[] {
  if (target.parentId === null) {
    const next = layers.slice();
    next.splice(clampIndex(target.index, next.length), 0, layer);
    return next;
  }
  return mapLayer(layers, target.parentId, (parent) => {
    if (!isGroup(parent)) return parent;
    const children = parent.children.slice();
    children.splice(clampIndex(target.index, children.length), 0, layer);
    return { ...parent, children };
  });
}

/**
 * Moves a layer to another position, possibly into or out of a group. Moving a
 * group into its own subtree would detach the document, so it is refused.
 */
export function moveLayerInTree(
  layers: readonly Layer[],
  layerId: string,
  target: InsertTarget,
): Layer[] {
  const location = locateLayer(layers, layerId);
  if (!location) return layers as Layer[];
  if (isGroup(location.layer) && target.parentId && containsLayer(location.layer, target.parentId)) {
    return layers as Layer[];
  }

  const sameParent = (location.parent?.id ?? null) === target.parentId;
  const detached = removeLayerFromTree(layers, layerId);
  if (!detached.removed) return layers as Layer[];

  // Removing the layer first shifts every later sibling down by one.
  const index = sameParent && target.index > location.index ? target.index - 1 : target.index;
  return insertLayerAt(detached.layers, { parentId: target.parentId, index }, detached.removed);
}

/** Deep copy with fresh ids, used by Duplicate Layer and Duplicate Group. */
export function cloneLayerTree(layer: Layer, makeId: () => string): Layer {
  if (!isGroup(layer)) return { ...layer, id: makeId() };
  return {
    ...layer,
    id: makeId(),
    children: layer.children.map((child) => cloneLayerTree(child, makeId)),
  };
}

/** Names are unique across the whole tree so the layers panel stays readable. */
export function nextLayerNameInTree(layers: readonly Layer[], base = 'Layer'): string {
  const used = new Set(flattenLayers(layers).map((layer) => layer.name));
  for (let i = countLayers(layers) + 1; ; i += 1) {
    const candidate = `${base} ${i}`;
    if (!used.has(candidate)) return candidate;
  }
}

function clampIndex(index: number, length: number): number {
  return Math.max(0, Math.min(length, Math.round(index)));
}
